import React, { useMemo } from 'react';
import { formatDate } from 'util/datelib';
import { Scrollbars } from 'react-custom-scrollbars-2';
import { renderTrack, renderThumb, renderView } from 'components/scrollbar/Scrollbar';
import { t } from 'i18next';
import Badge from 'widgets/shared/badge';
import { getAlertStateColor } from 'util/status';
import { Timeline } from 'api/types/alerts';

interface TimelineProps {
  events: Timeline[];
  className?: string;
  onEventClick?: (event: Timeline) => void;
  showAllEvents?: boolean;
}

const Timelines: React.FC<TimelineProps> = ({ 
  events, 
  className = "",
  onEventClick,
  showAllEvents = true,
}) => {

  // Filter and sort events
  const filteredAndSortedEvents = useMemo(() => {
    let filteredEvents = events;
    
    if (!showAllEvents) {
      // Filter out events where action is "notification"
      filteredEvents = events.filter(event => {
        const isNotification = event.action === 'NOTIFICATION';
        return !isNotification;
      });
    }
    
    // Sort events by created date (newest first)
    return [...filteredEvents].sort((a, b) => 
      new Date(b.created).getTime() - new Date(a.created).getTime()
    );
  }, [events, showAllEvents]);

  const handleEventClick = (event: Timeline) => {
    if (onEventClick) {
      onEventClick(event);
    }
  };

  return (
    <div className={`${className}`}>
      <Scrollbars
        autoHide
        renderTrackVertical={renderTrack}
        renderThumbVertical={renderThumb}
        renderView={renderView}
      >
        <div className="flex flex-col gap-4 px-4 py-6 relative">
          {filteredAndSortedEvents.map((event, index) => (
            <div 
              key={index} 
              className="flex items-start gap-4 relative"
              onClick={() => handleEventClick(event)}
              style={{ cursor: onEventClick ? 'pointer' : 'default' }}
            >
              {/* Timeline line and dot */}
              <div className="flex flex-col items-center flex-shrink-0 relative w-6">
                <div className="w-3 h-3 top-10 rounded-full border-2 border-background-50 z-10 relative"></div>
                {index < filteredAndSortedEvents.length - 1 && (
                  <div className="absolute top-[3.65rem] left-1/2 transform -translate-x-1/2 w-0.5 h-20 bg-background-100"></div>
                )}
              </div>
              
              {/* Timeline content */}
              <div className={`bg-gray-100 flex-1 p-4 rounded-lg ${onEventClick ? 'hover:bg-background-200 transition-colors duration-200' : ''}`}>
                <div className="flex justify-between items-center">
                  <div className="text-txt-sm font-medium text-primary">
                  {formatDate(event.created, "longWithTime")}
                  <div className="flex-1">
                    <div className="text-txt-xs text-primary font-bold">
                      {event.user ? event.user : event.username}
                    </div>
                    {event.data && typeof event.data === 'object' && Object.keys(event.data).length > 0 ? (
                      <div className="text-txt-xs text-secondary">
                        {t("alert.timeline.event.data", { type: event.data?.type })}: {event.data?.value} {event.data?.unit}
                      </div>
                    ) : (
                      <div className="text-txt-xs text-secondary">
                        {'-'}
                      </div>
                    )}
                  </div>
                  </div>
                  <div>
                    <Badge
                      sizes="sm"
                      variant="fill"
                      label={event.action}
                      color={getAlertStateColor(event.action)}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Scrollbars>
    </div>
  );
};

export default Timelines; 